<template>
  <ClientOnly>
    <el-tooltip :id="'unstakeTooltip'" :disabled="!disabled">
      <template #content>{{ $t('unstakeBtnFundsToUnlock') }} </template>
      <button
        class="btn-primary small"
        :loading="loadingUnlock"
        :disabled="disabled || loadingUnlock || isAlreadyUnstaked"
        @click="unlockTokens(tokenAddress, index)"
      >
        {{ loadingUnlock ? $t('unstakeBtnUnlocking') : $t('unstakeBtnUnlock') }}
      </button>
    </el-tooltip>
  </ClientOnly>
</template>

<script setup lang="ts">
import { getCancelErrorMessage, modifyHistoryHashStatus, saveHashToLocalStorage } from '~/utils';
import PearlApiService from '~/composables/PearlApiService';
import useEnvs from '~/composables/useEnvs';
import { useWalletInfo, useWeb3ModalAccount, useWeb3ModalProvider } from '@web3modal/ethers/vue';

const { t } = useI18n();

const emit = defineEmits(['update']);

const unstaked = useCookie<string[]>('unstaked');
const store = useMainStore();
const { apiUrl } = useEnvs();
const { address } = useWeb3ModalAccount();
const { walletProvider } = useWeb3ModalProvider();
const { walletInfo } = useWalletInfo();
const { unlock } = useStakingMethods();
const { $errorHandler } = useNuxtApp();
const loadingUnlock = ref(false);

const props = defineProps<{
  index: number;
  tokenAddress: string;
  disabled: boolean;
}>();

const isAlreadyUnstaked = computed(() => {
  const unstakedId = `${props.index}_${address.value}_${props.tokenAddress}`;
  return unstaked.value?.includes(unstakedId);
});

async function unlockTokens(tokenAddress: string, index: number) {
  if (!walletProvider.value || !address.value) {
    alert(t('appStatisticUnlockWalletAlert'));
    return;
  }

  try {
    loadingUnlock.value = true;

    addUnstakedToCookie(index);

    const receipt = await unlock(tokenAddress, index);

    if (!receipt?.hash) return;

    await PearlApiService.pushActivity(apiUrl, {
      address: address.value ?? '',
      transactionHash: receipt.hash,
      value: 0,
      type: 'STAKE_UNLOCK'
    });

    saveHashToLocalStorage(t('appStatisticWithdrawUnlocked'), receipt.hash);
    store.updateVersion();

    await receipt?.wait();

    modifyHistoryHashStatus(receipt.hash, 'Done');
    store.updateVersion();
    emit('update');
  } catch (error: unknown) {
    removeUnstakedFromCookie(index);

    if (walletInfo.value?.name) {
      const errorMessage = getCancelErrorMessage(error, walletInfo.value.name);
      $errorHandler(t('notificationFailedToUnlock') + t(errorMessage));
    }
    console.error(error);
  } finally {
    loadingUnlock.value = false;
  }
}

function addUnstakedToCookie(index: number) {
  const unstakedId = `${index}_${address.value}_${props.tokenAddress}`;

  unstaked.value = [...unstaked.value, unstakedId];
}

function removeUnstakedFromCookie(index: number) {
  const unstakedId = `${index}_${address.value}_${props.tokenAddress}`;
  if (!unstakedId || !unstaked.value) {
    return;
  }

  unstaked.value = unstaked.value.filter((id) => id !== unstakedId);
}
</script>

<style scoped lang="scss">
.small {
  padding: 8px 12px;
  white-space: nowrap;
}
</style>
